import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  User,
  MapPin,
  CreditCard,
  Package,
  Truck,
  Download,
  FileText,
  Printer,
  Clock,
  Check,
  CheckCircle,
  XCircle,
  MessageCircle,
} from 'lucide-react';
import { ordersApi } from '../../api/endpoints/orders';
import { Select } from '../../components/ui/Select';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { CurrencyDisplay } from '../../components/ui/CurrencyDisplay';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { SlideOver } from '../../components/ui/SlideOver';
import { useToast } from '../../components/ui/Alert';
import { formatDateTime, getCdnUrl } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';
import { DELIVERY_STATUSES, PAYMENT_STATUSES, SHIPPING_STATUSES } from '../../lib/constants';
import { CreateShippingLabelModal } from './CreateShippingLabelModal';
import { OrderItemFields } from './OrderItemFields';
import type { PaymentStatus } from '../../types/order';

interface OrderDetailsSlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string | null;
  storeId: string;
}

export function OrderDetailsSlideOver({ isOpen, onClose, orderId, storeId }: OrderDetailsSlideOverProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentStore } = useStore();

  const [trackingInput, setTrackingInput] = useState('');
  const [shippingCompanyInput, setShippingCompanyInput] = useState('');
  const [isEditingTracking, setIsEditingTracking] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', storeId, orderId],
    queryFn: () => ordersApi.get(storeId, orderId!),
    enabled: !!storeId && !!orderId && isOpen,
  });

  // Backend marks the order as seen during GET /:orderId. Once that fetch
  // resolves, the cached orders list still has seen:false — invalidate so
  // the "New" badge and unseen counter reflect the new server state.
  useEffect(() => {
    if (!order?._id) return;
    queryClient.invalidateQueries({ queryKey: ['orders', storeId] });
    queryClient.invalidateQueries({ queryKey: ['orders-unseen', storeId] });
    queryClient.invalidateQueries({ queryKey: ['orders-status-counts', storeId] });
  }, [order?._id, queryClient, storeId]);

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      ordersApi.update(storeId, orderId!, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', storeId, orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders', storeId] });
      toast('success', t('orders.updateSuccess'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const bankTransferMutation = useMutation({
    mutationFn: (action: 'approve' | 'reject') =>
      ordersApi.reviewBankTransfer(storeId, orderId!, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', storeId, orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders', storeId] });
      toast('success', t('orders.updateSuccess'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const timeline = useMemo(() => {
    if (!order) return [];

    const iconMap: Record<string, React.ReactNode> = {
      created: <Clock className="h-4 w-4" />,
      payment_status_changed: <CreditCard className="h-4 w-4" />,
      delivery_status_changed: <Check className="h-4 w-4" />,
      tracking_added: <Package className="h-4 w-4" />,
      note_updated: <FileText className="h-4 w-4" />,
      address_updated: <MapPin className="h-4 w-4" />,
      shipping_carrier_changed: <Truck className="h-4 w-4" />,
      bank_transfer_approved: <CreditCard className="h-4 w-4" />,
      bank_transfer_rejected: <CreditCard className="h-4 w-4" />,
    };

    const describeAction = (entry: { action: string; from?: string; to?: string; value?: string }) => {
      switch (entry.action) {
        case 'created': return t('orderTimeline.created');
        case 'payment_status_changed': return t('orderTimeline.paymentChanged', { status: t(`orders.${entry.to}`, entry.to ?? '') });
        case 'delivery_status_changed': return t('orderTimeline.statusChanged', { status: t(`orders.${entry.to}`, entry.to ?? '') });
        case 'tracking_added': return `${t('orderTimeline.trackingAdded')}: ${entry.value}`;
        case 'note_updated': return t('orderTimeline.noteAdded');
        case 'address_updated': return t('orderTimeline.addressUpdated', 'تم تحديث العنوان');
        case 'shipping_carrier_changed': return t('orderTimeline.shippingCarrierChanged', { carrier: entry.to }) || `شركة الشحن: ${entry.to}`;
        case 'bank_transfer_approved': return t('orderTimeline.bankTransferApproved', 'تم قبول التحويل البنكي');
        case 'bank_transfer_rejected': return t('orderTimeline.bankTransferRejected', 'تم رفض التحويل البنكي');
        default: return entry.action;
      }
    };

    // Use real activity log if available
    if (order.activity && order.activity.length > 0) {
      return order.activity.map(entry => ({
        icon: iconMap[entry.action] || <Clock className="h-4 w-4" />,
        description: describeAction(entry),
        timestamp: entry.at,
      }));
    }

    // Fallback for old orders without activity log
    const entries: { icon: React.ReactNode; description: string; timestamp: string }[] = [];
    entries.push({ icon: <Clock className="h-4 w-4" />, description: t('orderTimeline.created'), timestamp: order.createdAt });
    const displayStatus = order.delivery || order.status;
    entries.push({ icon: <Check className="h-4 w-4" />, description: t('orderTimeline.statusChanged', { status: t(`orders.${displayStatus}`, displayStatus) }), timestamp: order.createdAt });
    entries.push({ icon: <CreditCard className="h-4 w-4" />, description: t('orderTimeline.paymentChanged', { status: t(`orders.${order.paymentStatus}`, order.paymentStatus) }), timestamp: order.createdAt });
    return entries;
  }, [order, t]);

  const handleDownloadInvoice = async () => {
    if (!storeId || !orderId) return;
    setDownloadingInvoice(true);
    try {
      const blob = await ordersApi.downloadInvoice(storeId, orderId);
      const filename = `invoice-${displayNumber || orderId}.pdf`;
      if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
        try {
          const file = new File([blob], filename, { type: 'application/pdf' });
          await navigator.share({ files: [file], title: t('orders.downloadInvoice', 'Invoice') });
          return;
        } catch { /* fallback */ }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast('error', t('common.error'));
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handleSaveTracking = () => {
    if (!trackingInput.trim()) return;
    updateMutation.mutate(
      { trackingNumber: trackingInput.trim(), shippingCarrier: shippingCompanyInput.trim() || undefined },
      { onSuccess: () => setIsEditingTracking(false) } as any,
    );
  };

  if (!isOpen) return null;

  const currency = order?.currency || currentStore?.currency || 'SAR';
  const displayNumber = order?.orderNumber ?? order?._id?.slice?.(-6) ?? '';
  const showTrackingForm = order && (!order.trackingId || isEditingTracking);
  const isDigitalOrder = order?.allItemsAreAutoDelivered
    || (order?.items && order.items.length > 0 && order.items.every(i => i.productType === 'code' || i.productType === 'file' || i.productType === 'digital'));

  return (
    <>
      <SlideOver
        isOpen={isOpen}
        onClose={onClose}
        title={order ? `${t('orders.orderDetails')} #${displayNumber}` : t('orders.orderDetails')}
        size="xl"
      >
        {isLoading || !order ? (
          <div className="flex items-center justify-center py-16"><LoadingSpinner /></div>
        ) : (
          <>
            {/* Header badges + actions */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <StatusBadge status={order.delivery || order.status} />
              <StatusBadge status={order.paymentStatus} />
              <span className="text-sm text-text-muted ms-auto">{formatDateTime(order.createdAt)}</span>
              <Button variant="secondary" size="sm" onClick={handleDownloadInvoice} isLoading={downloadingInvoice}>
                <Download className="h-4 w-4" />
                {t('orders.downloadInvoice', 'Invoice')}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => window.print()}>
                <Printer className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
              {/* Main content */}
              <div className="lg:col-span-2 space-y-6">
                {/* Items */}
                <section className="rounded-xl border border-border-default bg-bg-tertiary p-4">
                  <h2 className="flex items-center gap-2 text-sm font-semibold mb-3">
                    <Package className="h-4 w-4 text-accent" />
                    {t('orders.items')}
                  </h2>
                  <div className="space-y-2">
                    {(order.items || []).map((item, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-lg border border-border-default/50 bg-bg-secondary p-3">
                        {item.image ? (
                          <img src={getCdnUrl(item.image, currentStore?._id)} alt="" className="h-10 w-10 rounded-lg object-cover" />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-bg-hover" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.title}</p>
                          {item.variantTitle && <p className="text-xs text-text-muted">{item.variantTitle}</p>}
                          <OrderItemFields fields={item.fields} />
                        </div>
                        <div className="text-end">
                          <p className="text-xs text-text-muted"><CurrencyDisplay amount={item.price} currency={currency} /> x {item.quantity}</p>
                          <p className="text-sm font-medium"><CurrencyDisplay amount={item.total} currency={currency} /></p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary */}
                  <div className="mt-3 space-y-1.5 border-t border-border-default pt-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">{t('orders.subtotal')}</span>
                      <CurrencyDisplay amount={order.subtotal} currency={currency} />
                    </div>
                    {order.discount != null && order.discount > 0 && (
                      <div className="flex justify-between text-sm text-success">
                        <span>{t('orders.discount')}</span>
                        <span>-<CurrencyDisplay amount={order.discount} currency={currency} /></span>
                      </div>
                    )}
                    {order.shippingCost != null && order.shippingCost > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">{t('orders.shipping')}</span>
                        <CurrencyDisplay amount={order.shippingCost} currency={currency} />
                      </div>
                    )}
                    {order.tax != null && order.tax > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">{t('orders.tax')}</span>
                        <CurrencyDisplay amount={order.tax} currency={currency} />
                      </div>
                    )}
                    <div className="flex justify-between text-base font-bold border-t border-border-default pt-2">
                      <span>{t('common.total')}</span>
                      <CurrencyDisplay amount={order.total} currency={currency} />
                    </div>
                  </div>
                </section>

                {/* Notes */}
                {order.notes && (
                  <section className="rounded-xl border border-border-default bg-bg-tertiary p-4">
                    <h2 className="text-sm font-semibold mb-2">{t('orders.notes')}</h2>
                    <p className="text-sm text-text-secondary whitespace-pre-wrap">{order.notes}</p>
                  </section>
                )}

                {/* Timeline */}
                <section className="rounded-xl border border-border-default bg-bg-tertiary p-4">
                  <h2 className="flex items-center gap-2 text-sm font-semibold mb-3">
                    <Clock className="h-4 w-4 text-accent" />
                    {t('orderTimeline.created', 'Order Timeline')}
                  </h2>
                  <div className="relative">
                    {timeline.map((entry, i) => (
                      <div key={i} className="relative flex gap-3 pb-4 last:pb-0">
                        {i < timeline.length - 1 && <div className="absolute start-[13px] top-7 bottom-0 w-px bg-border-default" />}
                        <div className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border-default bg-bg-secondary text-text-secondary">
                          {entry.icon}
                        </div>
                        <div className="flex-1 pt-0.5">
                          <p className="text-sm text-text-primary">{entry.description}</p>
                          <p className="text-xs text-text-muted mt-0.5">{formatDateTime(entry.timestamp)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </div>

              {/* Sidebar */}
              <div className="space-y-4">
                {/* Status updates */}
                <section className="rounded-xl border border-border-default bg-bg-tertiary p-4 space-y-3">
                  <h2 className="text-sm font-semibold">{t('orders.updateStatus')}</h2>
                  <Select
                    label={t('common.status')}
                    options={DELIVERY_STATUSES.map((s) => ({ value: s, label: t(`orders.${s}`, s) }))}
                    value={order.delivery || order.status}
                    onChange={(e) => updateMutation.mutate({ delivery: e.target.value })}
                  />
                  {order.paymentMethod === 'bankTransfer' ? (
                    <Select
                      label={t('orders.paymentStatus')}
                      options={PAYMENT_STATUSES.map((s) => ({ value: s, label: t(`orders.${s}`, s) }))}
                      value={order.paymentStatus}
                      onChange={(e) => updateMutation.mutate({ paymentStatus: e.target.value as PaymentStatus })}
                    />
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">{t('orders.paymentStatus')}</label>
                      <div className="px-3 py-2 bg-bg-secondary rounded-lg text-sm text-text-primary">{t(`orders.${order.paymentStatus}`, order.paymentStatus)}</div>
                    </div>
                  )}
                  {!isDigitalOrder && (
                    <Select
                      label={t('orders.shippingStatus')}
                      options={SHIPPING_STATUSES.map((s) => ({ value: s, label: t(`orders.${s}`, s) }))}
                      value={order.shippingStatus}
                      onChange={(e) => updateMutation.mutate({ shippingStatus: e.target.value })}
                    />
                  )}
                </section>

                {/* Shipping / Tracking - hidden for digital-only orders */}
                {!isDigitalOrder && <section className="rounded-xl border border-border-default bg-bg-tertiary p-4">
                  <h2 className="flex items-center gap-2 text-sm font-semibold mb-2">
                    <Truck className="h-4 w-4 text-accent" />
                    {t('orders.shipping')}
                  </h2>
                  <div className="space-y-2">
                    {order.trackingId && !isEditingTracking && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-text-secondary">{t('orderTimeline.trackingNumber')}</span>
                        <button onClick={() => { setTrackingInput(order.trackingId || ''); setShippingCompanyInput(order.shippingCompany || ''); setIsEditingTracking(true); }} className="font-mono text-accent hover:underline">
                          {order.trackingId}
                        </button>
                      </div>
                    )}
                    {order.shippingCompany && !isEditingTracking && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-text-secondary">{t('orders.shippingCarrier')}</span>
                        <span>{order.shippingCompany}</span>
                      </div>
                    )}

                    {showTrackingForm && (
                      <div className="space-y-2 border-t border-border-default pt-2">
                        <Input label={t('orderTimeline.trackingNumber')} placeholder={t('orderTimeline.enterTracking')} value={trackingInput} onChange={(e) => setTrackingInput(e.target.value)} />
                        <Input label={t('orders.shippingCarrier')} value={shippingCompanyInput} onChange={(e) => setShippingCompanyInput(e.target.value)} />
                        <div className="flex gap-2">
                          <Button variant="primary" size="sm" className="flex-1" onClick={handleSaveTracking} isLoading={updateMutation.isPending} disabled={!trackingInput.trim()}>
                            {t('orderTimeline.saveTracking')}
                          </Button>
                          {isEditingTracking && (
                            <Button variant="secondary" size="sm" onClick={() => setIsEditingTracking(false)}>{t('common.cancel', 'Cancel')}</Button>
                          )}
                        </div>
                      </div>
                    )}

                    {order.shippingLabel && (
                      <a href={order.shippingLabel} target="_blank" rel="noopener noreferrer">
                        <Button variant="secondary" size="sm" className="w-full mt-1">
                          <Download className="h-4 w-4" />
                          {t('orders.downloadLabel')}
                        </Button>
                      </a>
                    )}
                    {!order.shippingLabel && order.shippingAddress && ['pending', 'processing', 'completed'].includes(order.status) && (
                      <Button variant="secondary" size="sm" className="w-full mt-1" onClick={() => setShowLabelModal(true)}>
                        <FileText className="h-4 w-4" />
                        {t('orders.createLabel')}
                      </Button>
                    )}
                  </div>
                </section>}

                {/* Customer */}
                {order.customer && (
                  <section className="rounded-xl border border-border-default bg-bg-tertiary p-4">
                    <h2 className="flex items-center gap-2 text-sm font-semibold mb-2">
                      <User className="h-4 w-4 text-accent" />
                      {t('orders.customer')}
                    </h2>
                    <div className="space-y-1 text-sm">
                      {order.customer.name && <p>{order.customer.name}</p>}
                      {order.customer.email && <p className="text-text-muted">{order.customer.email}</p>}
                      {order.customer.phone && (
                        <p className="text-text-muted flex items-center gap-2">
                          {order.customer.phone}
                          <a href={`https://wa.me/${order.customer.phone.replace(/\+/g, '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex text-[#25D366] hover:opacity-80">
                            <MessageCircle className="h-4 w-4" />
                          </a>
                        </p>
                      )}
                    </div>
                  </section>
                )}

                {/* Address - hidden for digital-only orders */}
                {!isDigitalOrder && order.shippingAddress && (
                  <section className="rounded-xl border border-border-default bg-bg-tertiary p-4">
                    <h2 className="flex items-center gap-2 text-sm font-semibold mb-2">
                      <MapPin className="h-4 w-4 text-accent" />
                      {t('orders.shippingAddress')}
                    </h2>
                    <div className="space-y-1 text-sm text-text-secondary">
                      {order.shippingAddress.name && <p>{order.shippingAddress.name}</p>}
                      {order.shippingAddress.street && <p>{order.shippingAddress.street}</p>}
                      <p>{[order.shippingAddress.city, order.shippingAddress.state, order.shippingAddress.zipCode].filter(Boolean).join(', ')}</p>
                      {order.shippingAddress.country && <p>{order.shippingAddress.country}</p>}
                      {order.shippingAddress.phone && <p>{order.shippingAddress.phone}</p>}
                    </div>
                  </section>
                )}

                {/* Payment + Bank Transfer */}
                <section className="rounded-xl border border-border-default bg-bg-tertiary p-4">
                  <h2 className="flex items-center gap-2 text-sm font-semibold mb-2">
                    <CreditCard className="h-4 w-4 text-accent" />
                    {t('orders.paymentStatus')}
                  </h2>
                  <StatusBadge status={order.paymentStatus} />
                  {order.couponCode && (
                    <p className="mt-2 text-sm text-text-muted">{t('orders.discount')}: <code className="text-accent">{order.couponCode}</code></p>
                  )}

                  {order.paymentMethod === 'bankTransfer' && (
                    <div className="mt-3 border-t border-border-default pt-3 space-y-2">
                      <p className="text-sm font-medium">{t('orders.bankTransfer', 'Bank Transfer')}</p>
                      {order.bankTransferReceipt && (
                        <img
                          src={getCdnUrl(order.bankTransferReceipt, currentStore?._id, 'products', 1280)}
                          alt={t('orders.receipt', 'Receipt')}
                          className="w-full max-h-48 rounded-lg object-contain border border-border-default cursor-pointer"
                          onClick={() => window.open(getCdnUrl(order.bankTransferReceipt!, currentStore?._id, 'products', null), '_blank')}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                          order.bankTransferStatus === 'approved' ? 'bg-success/15 text-success'
                            : order.bankTransferStatus === 'rejected' ? 'bg-danger/15 text-danger'
                              : 'bg-warning/15 text-warning'
                        }`}>
                          {order.bankTransferStatus === 'approved' ? t('orders.transferApproved', 'Approved')
                            : order.bankTransferStatus === 'rejected' ? t('orders.transferRejected', 'Rejected')
                              : t('orders.transferPending', 'Pending Review')}
                        </span>
                      </div>
                      {order.bankTransferStatus === 'pending' && (
                        <div className="flex gap-2">
                          <Button variant="primary" size="sm" className="flex-1" onClick={() => bankTransferMutation.mutate('approve')} isLoading={bankTransferMutation.isPending}>
                            <CheckCircle className="h-4 w-4" />
                            {t('orders.approveTransfer', 'Approve')}
                          </Button>
                          <Button variant="secondary" size="sm" className="flex-1 border-danger/30 text-danger hover:bg-danger/5" onClick={() => bankTransferMutation.mutate('reject')} isLoading={bankTransferMutation.isPending}>
                            <XCircle className="h-4 w-4" />
                            {t('orders.rejectTransfer', 'Reject')}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </section>
              </div>
            </div>
          </>
        )}
      </SlideOver>

      {/* Nested label creation modal */}
      <CreateShippingLabelModal
        isOpen={showLabelModal}
        onClose={() => setShowLabelModal(false)}
        orderId={orderId}
        storeId={storeId}
        paymentStatus={order?.paymentStatus}
      />
    </>
  );
}
